import { App, Tag, Tooltip, Typography } from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { evimoTableActionsColumn } from '~/app/components/common/EvimoTable/evimoTableColumnHelpers';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { TableRowActionItem } from '~/app/components/common/TableRowActionsMenu/types';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import type { KmsLinkedDocumentItem } from '~/app/services/kms/kms-document.types';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import {
  buildKmsDocumentDetailPath,
  formatKmsDocumentTypeLabel,
} from '~/app/services/kms/kms-document.utils';

const { Text } = Typography;

type UseKmsLibraryColumnsParams = {
  organizationId: string | null;
  isSuperAdmin: boolean;
};

export function useKmsLibraryColumns({
  organizationId,
  isSuperAdmin,
}: UseKmsLibraryColumnsParams): ColumnsType<KmsLinkedDocumentItem> {
  const { t } = useTranslation('kms');
  const { notification } = App.useApp();
  const navigate = useNavigate();

  const openContent = async (
    documentId: string,
    inline: boolean,
  ): Promise<void> => {
    try {
      await KmsDocumentService.openContent({
        documentId,
        organizationId,
        isSuperAdmin,
        inline,
      });
    } catch (error) {
      notification.error({
        message: t('detail.contentError'),
        description:
          error instanceof Error ? error.message : t('detail.contentError'),
      });
    }
  };

  return [
    {
      title: t('hitl.columns.document'),
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: { showTitle: false },
      width: 260,
      render: (fileName: string, row) => (
        <Tooltip title={fileName}>
          <Link
            to={buildKmsDocumentDetailPath(row.documentId, organizationId)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <FileTextOutlined />
            <Text strong ellipsis style={{ maxWidth: 200 }}>
              {fileName}
            </Text>
          </Link>
        </Tooltip>
      ),
    },
    {
      title: t('hitl.columns.aiSuggestion'),
      dataIndex: 'classifiedDocumentType',
      key: 'aiType',
      width: 160,
      ellipsis: true,
      render: (type?: string | null) =>
        type ? (
          <Tooltip title={type}>
            <Tag color="blue">{formatKmsDocumentTypeLabel(type)}</Tag>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('library.manualTypeColumn'),
      dataIndex: 'documentTypeHint',
      key: 'manualType',
      width: 160,
      ellipsis: true,
      render: (type?: string | null, row?: KmsLinkedDocumentItem) => {
        if (!type) {
          return <Text type="secondary">—</Text>;
        }
        const differsFromAi =
          Boolean(row?.classifiedDocumentType) &&
          row!.classifiedDocumentType !== type;
        return (
          <Tooltip title={type}>
            <Tag color={differsFromAi ? 'orange' : 'default'}>
              {formatKmsDocumentTypeLabel(type)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('library.statusColumn'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => {
        const label = t(`statusFilter.${status}`, {
          defaultValue: status,
        });
        return (
          <Tag
            color={
              status === 'linked'
                ? 'green'
                : status === 'pending_review'
                  ? 'orange'
                  : status === 'write_failed'
                    ? 'red'
                    : 'blue'
            }
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: t('library.scopeColumn'),
      key: 'scope',
      width: 180,
      ellipsis: { showTitle: false },
      render: (_: unknown, document: KmsLinkedDocumentItem) =>
        document.assetId ? (
          <Tooltip title={document.assetName ?? document.assetId}>
            <Text>
              {document.assetName?.trim() ||
                t('library.linkedAsset', {
                  assetId: `${document.assetId.slice(0, 8)}…`,
                })}
            </Text>
          </Tooltip>
        ) : (
          <Tag>{t('library.orgWideBadge')}</Tag>
        ),
    },
    {
      title: t('hitl.columns.uploadedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: string) => new Date(createdAt).toLocaleString(),
    },
    {
      title: t('library.actionsColumn'),
      key: 'actions',
      fixed: 'right',
      ...evimoTableActionsColumn,
      render: (_: unknown, row: KmsLinkedDocumentItem) => {
        const menuItems: TableRowActionItem[] = [
          {
            key: 'read',
            icon: <ReadOutlined />,
            label: t('library.readDocument'),
            onClick: () => {
              void openContent(row.documentId, true);
            },
          },
          {
            key: 'download',
            icon: <DownloadOutlined />,
            label: t('library.downloadDocument'),
            onClick: () => {
              void openContent(row.documentId, false);
            },
          },
          {
            key: 'details',
            icon: <EyeOutlined />,
            label: t('library.viewDetails'),
            onClick: () => {
              navigate(
                buildKmsDocumentDetailPath(row.documentId, organizationId),
              );
            },
          },
        ];

        return (
          <TableRowActionsMenu
            ariaLabel={t('library.actionsColumn')}
            items={menuItems}
          />
        );
      },
    },
  ];
}
